import Space from '@rebass/space'
import { Link, graphql } from 'gatsby'
import { GatsbyImage, getImage } from 'gatsby-plugin-image'
import React from 'react'
import styled from 'styled-components'
import { useQueryParam, ModalStateParam } from 'use-query-params'
import Button from '../components/Button'
import TrialDownloadButton from '../components/TrialDownloadButton'
import Badge from '../components/Badge'
import Card from '../components/Card'
import CompanyLogos from '../components/CompanyLogos'
import Container from '../components/Container'
import { Flex, Box } from '@rebass/grid'
import GradientWrapper from '../components/GradientWrapper'
import SEO from '../components/SEO'
import { ThemeConsumer } from 'styled-components'
import MainHighlights from '../components/MainHighlights'
import SecondaryHighlights from '../components/SecondaryHighlights'

// icons
import LogoUI8 from '../components/logos/LogoUI8'
import LogoSmashicons from '../components/logos/LogoSmashicons'
import LogoGumroad from '../components/logos/LogoGumroad'
import LogoCraftwork from '../components/logos/LogoCraftwork'
import LogoVectopus from '../components/logos/LogoVectopus'
import UsecaseDesigners from '../components/icons/UsecaseDesigners'
import UsecaseEngineers from '../components/icons/UsecaseEngineers'
import UsecaseMarketeers from '../components/icons/UsecaseMarketeers'
import IconStars from '../components/icons/IconStars'

const StyledTestimonialCard = styled(Card)`
  border-color: #f1dcb1;
  background-color: #fdedcc;
  box-shadow: none;
  display: flex;
  flex-direction: column;

  &:hover {
    background-color: #fdedcc;
    box-shadow: none;
  }

  p:first-child {
    flex: 1;
  }

  p.small.faded {
    margin-top: auto;
  }
`

const IndexPage = ({ data }) => {
  const [modalState, setModalState] = useQueryParam(
    'thank-you',
    ModalStateParam,
  )

  function analyticsDownloadTrial() {
    setModalState('true')
  }

  return (
    <ThemeConsumer>
      {(theme) => (
        <>
          <SEO
            title="IconJar • Your icon organizer for macOS"
            description="The go-to app for managing your icon library. Say goodbye to scattered icons and hello to simple organization one click away! Download IconJar for macOS with free 14-day trial."
            meta={[
              {
                name: 'keywords',
                content:
                  'IconJar, icon organizer, macOS app, icon management, design tools, icon library, SVG icons, design workflow, icon organizer software, graphic design tools',
              },
              {
                property: 'og:url',
                content: 'https://geticonjar.com',
              },
              {
                name: 'twitter:site',
                content: '@geticonjar',
              },
            ]}
          />

          {/* Structured Data */}
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify({
                '@context': 'https://schema.org',
                '@type': 'SoftwareApplication',
                name: 'IconJar',
                alternateName: 'IconJar for macOS',
                description:
                  'The go-to app for managing your icon library. Say goodbye to scattered icons and hello to simple organization one click away!',
                url: 'https://geticonjar.com',
                applicationCategory: 'DesignApplication',
                operatingSystem: 'macOS',
                softwareVersion: '2.11.3',
                releaseNotes:
                  'Latest version with improved performance and new features',
                featureList: [
                  'Icon organization and management',
                  'Smart sets and collections',
                  'Drag and drop functionality',
                  'SVG optimization',
                  'Export capabilities',
                  'Search and filtering',
                  'Recently used icons',
                  'Starred favorites',
                  'Company logo integration',
                  'Testimonial system',
                ],
                systemRequirements: {
                  '@type': 'SystemRequirements',
                  operatingSystem: 'macOS 10.15 or later',
                  processorRequirements: 'Intel or Apple Silicon',
                  memoryRequirements: '4GB RAM minimum',
                  storageRequirements: '100MB available space',
                },
                offers: [
                  {
                    '@type': 'Offer',
                    name: 'Free Trial',
                    description: '14-day free trial of IconJar',
                    price: '0',
                    priceCurrency: 'USD',
                    availability: 'https://schema.org/InStock',
                    url: 'https://geticonjar.com',
                  },
                  {
                    '@type': 'Offer',
                    name: 'Annual Subscription',
                    description:
                      'IconJar annual subscription with regular updates and new features',
                    priceCurrency: 'USD',
                    price: '29.99',
                    priceValidUntil: '2025-12-31',
                    availability: 'https://schema.org/InStock',
                    billingIncrement: 'P1Y',
                    paymentAccepted: 'Credit Card, PayPal',
                    seller: {
                      '@type': 'Organization',
                      name: 'IconJar',
                      url: 'https://geticonjar.com',
                    },
                    url: 'https://geticonjar.com/pricing',
                  },
                  {
                    '@type': 'Offer',
                    name: 'Lifetime License',
                    description:
                      'One-time purchase for IconJar with major updates available as paid upgrades',
                    priceCurrency: 'USD',
                    price: '49.99',
                    priceValidUntil: '2025-12-31',
                    availability: 'https://schema.org/InStock',
                    paymentAccepted: 'Credit Card, PayPal',
                    seller: {
                      '@type': 'Organization',
                      name: 'IconJar',
                      url: 'https://geticonjar.com',
                    },
                    url: 'https://geticonjar.com/pricing',
                  },
                ],
                aggregateRating: {
                  '@type': 'AggregateRating',
                  ratingValue: '4.8',
                  reviewCount: '500',
                  bestRating: '5',
                  worstRating: '1',
                },
                brand: {
                  '@type': 'Brand',
                  name: 'IconJar',
                  url: 'https://geticonjar.com',
                },
                creator: {
                  '@type': 'Person',
                  name: 'Davey Heuser',
                  url: 'https://twitter.com/daveyheuser',
                },
                publisher: {
                  '@type': 'Organization',
                  name: 'IconJar',
                  url: 'https://geticonjar.com',
                },
                screenshot: 'https://geticonjar.com/hero@2x.png',
                downloadUrl: 'https://geticonjar.com/download',
                softwareHelp: 'https://geticonjar.com/support',
                license: 'Commercial License',
                copyrightYear: '2024',
                keywords: [
                  'icon management',
                  'macOS app',
                  'design tools',
                  'icon organizer',
                  'SVG icons',
                  'design workflow',
                  'icon library',
                  'graphic design',
                  'icon organization',
                  'design software',
                ],
                audience: {
                  '@type': 'Audience',
                  audienceType:
                    'Designers, Developers, Creative Professionals, UI/UX Designers, Graphic Designers',
                },
                isAccessibleForFree: true,
                hasOfferCatalog: {
                  '@type': 'OfferCatalog',
                  name: 'IconJar Pricing Plans',
                  itemListElement: [
                    {
                      '@type': 'Offer',
                      itemOffered: {
                        '@type': 'Product',
                        name: 'Free Trial',
                      },
                    },
                    {
                      '@type': 'Offer',
                      itemOffered: {
                        '@type': 'Product',
                        name: 'Annual Subscription',
                      },
                    },
                    {
                      '@type': 'Offer',
                      itemOffered: {
                        '@type': 'Product',
                        name: 'Lifetime License',
                      },
                    },
                  ],
                },
                mainEntity: {
                  '@type': 'WebPage',
                  name: 'IconJar Homepage',
                  description:
                    'Homepage for IconJar - the go-to app for managing your icon library on macOS',
                  url: 'https://geticonjar.com',
                  breadcrumb: {
                    '@type': 'BreadcrumbList',
                    itemListElement: [
                      {
                        '@type': 'ListItem',
                        position: 1,
                        name: 'Home',
                        item: 'https://geticonjar.com',
                      },
                    ],
                  },
                },
                potentialAction: {
                  '@type': 'DownloadAction',
                  target: {
                    '@type': 'EntryPoint',
                    urlTemplate: 'https://geticonjar.com/download',
                  },
                },
              }),
            }}
          />

          <Container my={[4, 5]}>
            <Box textAlign={'center'}>
              <h1>The go-to app for managing your icon library</h1>
              <p className="subtitle">
                Say goodbye to scattered icons and hello to simple organization
                one click away!
              </p>

              <Box mb={3}>
                <a
                  href="https://go.setapp.com/stp177?refAppID=264&utm_medium=vendor_program&utm_source=IconJar&utm_content=link&utm_campaign=hero_badge"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ textDecoration: 'none' }}>
                  <Badge colorscheme="white" clickable="true">
                    <IconStars style={{ marginRight: '8px' }} />
                    98% of our Setapp users recommend IconJar
                  </Badge>
                </a>
              </Box>

              <Space mx={2} mb={[3]}>
                <TrialDownloadButton
                  onClick={analyticsDownloadTrial}
                  colorscheme="blue"
                  className="xs-hide"
                  location="hero"
                />
                <Button as={Link} to="/pricing" colorscheme="green">
                  View pricing
                </Button>
              </Space>
              <Box
                style={{ width: '1264px', maxWidth: '100%' }}
                mt={3}
                mx={'auto'}>
                <GatsbyImage
                  image={getImage(
                    theme.appearance === 'light' ? data.hero : data.heroDark,
                  )}
                  alt="IconJar macOS app interface showing icon organization, smart sets, and drag-and-drop functionality"
                  className="app-preview"
                />
              </Box>
            </Box>
          </Container>

          <Container>
            <Flex flexWrap="wrap" justifyContent="center">
              <Box width={[1, 3 / 4]} mb={[5, 6]}>
                <CompanyLogos />
              </Box>
            </Flex>
          </Container>

          <GradientWrapper
            pt={[4, 5]}
            style={{
              maxWidth: '100%',
              overflowX: 'hidden',
              marginTop: '-48px',
            }}>
            <Container>
              {/* Drag + drop, Export, SVG optimizer */}
              <MainHighlights />

              {/* Smart sets, Recently used, Starred*/}
              <SecondaryHighlights />
            </Container>

            <Container mt={[4, 5]} mb={[5, 6]}>
              <Flex flexWrap="wrap">
                <Box width={[1, 1 / 3]} px={[0, 3, 3]} mb={[5, 0]}>
                  <Space pb={[3]}>
                    <h2>Did you say niche? We don't think so.</h2>
                  </Space>
                  <Flex justifySelf="stretch" mb={3}>
                    <Box width={'15%'}>
                      <UsecaseDesigners />
                    </Box>
                    <Box width={'85%'}>
                      <h6>Designers</h6>
                      <p>
                        Ensure that your icon collection doesn't block your
                        creative process.
                      </p>
                    </Box>
                  </Flex>
                  <Flex justifySelf="stretch" mb={3}>
                    <Box width={'15%'}>
                      <UsecaseEngineers />
                    </Box>
                    <Box width={'85%'}>
                      <h6>Engineers</h6>
                      <p>
                        Get rid of that bottleneck in the development process.
                      </p>
                    </Box>
                  </Flex>
                  <Flex justifySelf="stretch" mb={3}>
                    <Box width={'15%'}>
                      <UsecaseMarketeers />
                    </Box>
                    <Box width={'85%'}>
                      <h6>Marketeers</h6>
                      <p>
                        Use icons efficiently to create beautiful marketing
                        content.
                      </p>
                    </Box>
                  </Flex>
                </Box>
                <Box width={[1, 2 / 3]} px={[0, 3, 3]} mb={[4, 0]}>
                  <Flex flexWrap="wrap">
                    <Box width={[1, 1 / 2]} mb={3}>
                      <StyledTestimonialCard p={3} mx={[0, 2]}>
                        <p>
                          Very handy even as a starting point for composing /
                          planning out visuals and thinking around what
                          iconography is needed.
                        </p>
                        <p className="small faded">
                          Rosemary Lynch &bull; Jan 10, 2023
                        </p>
                      </StyledTestimonialCard>
                    </Box>
                    <Box width={[1, 1 / 2]} mb={3}>
                      <StyledTestimonialCard p={3} mx={[0, 2]}>
                        <p>
                          If you do any design work you need a good icon
                          library, and this is a good way to keep and maintain
                          one. Key feature: you can associate licenses with sets
                          of icons.
                        </p>
                        <p className="small faded">
                          Mathew Murphy &bull; Jul 25, 2023
                        </p>
                      </StyledTestimonialCard>
                    </Box>
                    <Box width={[1, 1 / 2, 1 / 3]} mb={3}>
                      <StyledTestimonialCard p={3} mx={[0, 2]}>
                        <p>works perfectly, AWESOME FOR MY WORKFLOW</p>
                        <p className="small faded">
                          Beck Graben &bull; Jan 26, 2023
                        </p>
                      </StyledTestimonialCard>
                    </Box>
                    <Box width={[1, 1 / 2, 1 / 3]} mb={3}>
                      <StyledTestimonialCard p={3} mx={[0, 2]}>
                        <p>
                          Great program, saves me the hassle of having to go to
                          each icon library's website
                        </p>
                        <p className="small faded">Nick &bull; Apr 27, 2023</p>
                      </StyledTestimonialCard>
                    </Box>
                    <Box width={[1, 1 / 2, 1 / 3]} mb={3}>
                      <StyledTestimonialCard p={3} mx={[0, 2]}>
                        <p>
                          Easy to use and stable app. This is like Typeface for
                          icons.
                        </p>
                        <p className="small faded">
                          Roland &bull; Apr 27, 2023
                        </p>
                      </StyledTestimonialCard>
                    </Box>
                  </Flex>
                </Box>
              </Flex>
            </Container>
          </GradientWrapper>

          <Container mb={[5, 6]}>
            <Flex flexWrap="wrap" alignItems="center" mx={2}>
              <Box width={1} mb={[2]} textAlign={'center'}>
                <h6>We're supported by:</h6>
              </Box>
              <Box width={[1, 1 / 2, 1 / 3]} mb={3}>
                <a href="https://www.ui8.net/?rel=icnjr">
                  <Card
                    as={Flex}
                    alignItems="center"
                    justifyContent="center"
                    style={{
                      height: '133px',
                      backgroundColor: '#2B292B',
                    }}
                    mx={2}
                    animateonhover="true">
                    <LogoUI8 width="48" height="48" />
                  </Card>
                </a>
              </Box>
              <Box width={[1, 1 / 2, 1 / 3]} mb={3}>
                <a href="https://iconfinder.com">
                  <Card
                    as={Flex}
                    alignItems="center"
                    justifyContent="center"
                    style={{
                      height: '133px',
                      backgroundColor: '#14984D',
                    }}
                    mx={2}
                    animateonhover="true">
                    <GatsbyImage
                      image={getImage(data.logoIconfinder)}
                      alt="Iconfinder logos"
                    />
                  </Card>
                </a>
              </Box>
              <Box width={[1, 1 / 2, 1 / 3]} mb={3}>
                <a href="https://smashicons.com">
                  <Card
                    as={Flex}
                    alignItems="center"
                    justifyContent="center"
                    style={{
                      height: '133px',
                      backgroundColor: '#2a2d2d',
                    }}
                    mx={2}
                    animateonhover="true">
                    <LogoSmashicons width="64" height="64" />
                  </Card>
                </a>
              </Box>

              <Box width={[1, 1 / 2, 1 / 3]} mb={[3, 3, 0]}>
                <a href="https://discover.gumroad.com/design/icons?a=581296979">
                  <Card
                    as={Flex}
                    alignItems="center"
                    justifyContent="center"
                    style={{
                      height: '133px',
                      backgroundColor: '#FFC900',
                    }}
                    mx={2}
                    animateonhover="true">
                    <LogoGumroad width="128" />
                  </Card>
                </a>
              </Box>
              <Box width={[1, 1 / 2, 1 / 3]} mb={[3, 3, 0]}>
                <a href="https://craftwork.design/?ref=333">
                  <Card
                    as={Flex}
                    alignItems="center"
                    justifyContent="center"
                    style={{
                      height: '133px',
                      backgroundColor: '#ececec',
                    }}
                    mx={2}
                    animateonhover="true">
                    <LogoCraftwork width="128" />
                  </Card>
                </a>
              </Box>
              <Box width={[1, 1 / 2, 1 / 3]} mb={[3, 3, 0]}>
                <a href="https://vectopus.com">
                  <Card
                    as={Flex}
                    alignItems="center"
                    justifyContent="center"
                    style={{
                      height: '133px',
                      backgroundColor: '#ffd00f',
                    }}
                    mx={2}
                    animateonhover="true">
                    <LogoVectopus width="128" />
                  </Card>
                </a>
              </Box>
            </Flex>
          </Container>

          <GradientWrapper pt={[5, 6]} style={{ backgroundSize: '100%' }}>
            <Container textAlign={'center'} mb={[5, 6]}>
              <Box
                style={{ width: '128px', marginTop: '-8px' }}
                mx="auto"
                mb={3}>
                <GatsbyImage
                  image={getImage(data.appIcon)}
                  alt="IconJar macOS app icon - Professional icon management and organization tool"
                />
              </Box>
              <h2>Let's get started</h2>
              <Space mx={2} mt={2} mb={[3, 0]}>
                <TrialDownloadButton
                  onClick={analyticsDownloadTrial}
                  colorscheme="white"
                  className="xs-hide"
                  location="footer_cta"
                />
                <Button as={Link} to="/pricing" colorscheme="green">
                  View pricing
                </Button>
              </Space>
            </Container>
          </GradientWrapper>
        </>
      )}
    </ThemeConsumer>
  )
}

export default IndexPage

export const query = graphql`
  query {
    hero: file(relativePath: { eq: "hero@2x.png" }) {
      childImageSharp {
        gatsbyImageData(
          width: 2528
          quality: 100
          placeholder: NONE
          layout: CONSTRAINED
          formats: [AUTO, WEBP]
        )
      }
    }
    heroDark: file(relativePath: { eq: "hero-dark@2x.png" }) {
      childImageSharp {
        gatsbyImageData(
          width: 2528
          quality: 100
          placeholder: NONE
          layout: CONSTRAINED
          formats: [AUTO, WEBP]
        )
      }
    }
    appIcon: file(relativePath: { eq: "app-icon@2x.png" }) {
      childImageSharp {
        gatsbyImageData(
          width: 128
          quality: 100
          placeholder: NONE
          formats: [AUTO, WEBP]
        )
      }
    }
    inspectorScreenshot: file(relativePath: { eq: "inspector@2x.png" }) {
      childImageSharp {
        gatsbyImageData(
          width: 2302
          quality: 100
          placeholder: NONE
          layout: CONSTRAINED
          formats: [AUTO, WEBP]
        )
      }
    }
    inspectorScreenshotDark: file(
      relativePath: { eq: "inspector-dark@2x.png" }
    ) {
      childImageSharp {
        gatsbyImageData(
          width: 2302
          quality: 100
          placeholder: NONE
          layout: CONSTRAINED
          formats: [AUTO, WEBP]
        )
      }
    }
    starredScreenshot: file(relativePath: { eq: "starred@2x.png" }) {
      childImageSharp {
        gatsbyImageData(
          width: 2302
          quality: 100
          placeholder: NONE
          layout: CONSTRAINED
          formats: [AUTO, WEBP]
        )
      }
    }
    starredScreenshotDark: file(relativePath: { eq: "starred-dark@2x.png" }) {
      childImageSharp {
        gatsbyImageData(
          width: 2302
          quality: 100
          placeholder: NONE
          layout: CONSTRAINED
          formats: [AUTO, WEBP]
        )
      }
    }
    logoIconfinder: file(relativePath: { eq: "logo-iconfinder@2x.png" }) {
      childImageSharp {
        gatsbyImageData(
          width: 151
          quality: 100
          placeholder: NONE
          formats: [AUTO, WEBP]
        )
      }
    }
    featureAnnouncement: file(
      relativePath: { eq: "updates/announcement-2.8.jpg" }
    ) {
      childImageSharp {
        gatsbyImageData(
          width: 252
          quality: 100
          placeholder: NONE
          formats: [AUTO, WEBP]
        )
      }
    }
    featureAnnouncementDark: file(
      relativePath: { eq: "updates/announcement-2.8-dark.jpg" }
    ) {
      childImageSharp {
        gatsbyImageData(
          width: 252
          quality: 100
          placeholder: NONE
          formats: [AUTO, WEBP]
        )
      }
    }
  }
`
